//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled from 'styled-components';

import { PrimaryAnchor } from '@components/atoms/anchor/Anchor';
import { RequiredPermissionsButton } from '@components/atoms/required-permissions-button/RequiredPermissionsButton';
import { CaptionSecondaryText } from '@components/atoms/text/Text';
import { RightsRequestOptions } from '@legacy-components/common/universal-image-actions/constants/universalImageActionsRightsRequestContstants';
import { IRightsOption } from '@legacy-components/rights-management/request-rights-model';
import Cuid from '@models/Cuid';
import { useMediaTrackingContext } from '@tracker/mediaTracking/contexts/MediaTrackingContext';
import { MediaTrackingEventName } from '@tracker/mediaTracking/mediaTrackingModel';
import {
  UIATrackingIds,
  UIA_RIGHTS_REQUEST_PERMISSION,
} from '@uia/constants/uiaConstants';
import { useMediaMetadataQuery } from '@uia/details/hooks/useMediaMetadataQuery';
import {
  MANAGE_RIGHTS_REQUEST_ARTICLE_URL,
  RIGHT_STATUSES_READ_ONLY_FORM,
  TIKTOK_RIGHTS_REQUEST_COPY_CHARACTER_LIMIT,
} from '@uia/rights/constants/uiaRightsConstants';
import { ExtendedRightsRequest } from '@uia/rights/forms/ExtendedRightsRequest';
import { useHasRightsFormErrorsQuery } from '@uia/rights/hooks/useHasRightsFormErrorsQuery';
import { useRightsConfigurationsQuery } from '@uia/rights/hooks/useRightsConfigurationsQuery';
import { copyTextToClipboard } from '@utils/copyToClipboard';
import { isDefined } from '@utils/filterPredicates';

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

export interface RequestRequiredForMissingTCUrlProps {
  cuid: Cuid;
  isReRequestRequiredForMissingTCUrl: boolean;
  requestType: RightsRequestOptions;
  setRightsRequestText: (value: string) => void;
  setSelectedOption: (selectedOption: IRightsOption) => void;
}

export const RequestRequiredForMissingTCUrl = ({
  cuid,
  isReRequestRequiredForMissingTCUrl,
  requestType,
  setRightsRequestText: _setRightsRequestText,
  setSelectedOption,
}: RequestRequiredForMissingTCUrlProps) => {
  const {
    data: metadata,
    isError: isErrorMetadata,
    isLoading: isLoadingMetadata,
  } = useMediaMetadataQuery(cuid);

  const {
    data: rightsOptions,
    isError: isErrorRightsConfigs,
    isLoading: isLoadingRightsConfigs,
  } = useRightsConfigurationsQuery(Cuid.toPartial(cuid), requestType);

  if (
    isLoadingMetadata ||
    isErrorMetadata ||
    isLoadingRightsConfigs ||
    isErrorRightsConfigs
  ) {
    return null;
  }

  const {
    rightsRequestStatus: {
      approvalPhrase: previousApprovalPhrase,
      communicationType,
      status,
    },
  } = metadata;

  return (
    <>
      {status !== RIGHTS_STATUS.EXPIRED &&
        isReRequestRequiredForMissingTCUrl && (
          <FlexStart>
            <RequiredPermissionsButton
              onClick={() => {
                const defaultOption = rightsOptions.find(
                  (item) => item.isDefault,
                );
                if (isDefined(defaultOption)) {
                  setSelectedOption(defaultOption);
                }
              }}
              permissionName={UIA_RIGHTS_REQUEST_PERMISSION}
              trackingId={UIATrackingIds.RIGHTS_REQUEST_REQUEST_AGAIN_BUTTON}
            >
              Request Rights Again
            </RequiredPermissionsButton>
            <CaptionSecondaryText>
              Requests are considered Rejected if the owner hasn't responded
              within 3 weeks.
            </CaptionSecondaryText>
          </FlexStart>
        )}
    </>
  );
};

const FlexStart = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    justify-content: start;
    align-items: start;
    gap: ${theme.spacings[2]};
  `,
);
